import { type MappaCatalogoLivelliDTO } from '@framework/api-client/api/users/schemas/mappaCatalogoLivelliDTO.ts';
import { modalConfirm } from '@framework/engine/ui/modal/modalConfirm.tsx';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { RadioGroupInput } from '@framework/form/inputs/RadioGroupInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { FlexRow } from '@framework/ui/components/row/FlexRow.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { Button, Modal, Radio, Space, Typography } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { getSelectAllKeys } from '../../layers/tree/mapLayersTreeKeys.ts';
import { LayerCatalogEditLinkButton } from './LayerCatalogEditLinkButton.tsx';
import { LayerCatalogImportSubmitButton } from './LayerCatalogImportSubmitButton.tsx';
import { LayerCatalogPreview } from './LayerCatalogPreview.tsx';
import { LayerCatalogSearchSelect } from './LayerCatalogSearchSelect.tsx';
import { filterCatalogLevelsBySelection } from './utils/filterCatalogLevelsBySelection.ts';
import {
  type ILayerCatalogLevels,
  parseLayerCatalogLevels,
} from './utils/parseLayerCatalogLevels.ts';

export type LayerCatalogImportMode = 'replace' | 'append';

// L'intera modale è un form: catalogo cercato, chiavi selezionate nell'albero
// e modalità di importazione sono tre campi. La selezione albero è un input di
// form (MapLayersTreeInput) e la modalità un RadioGroupInput.
const importFormSchema = yup.object({
  catalogId: yup.string().nullable(),
  selectedKeys: yup.array(yup.string().defined()).default([]),
  mode: yup.string().oneOf(['append', 'replace']).default('append'),
});

type IImportFormValues = yup.InferType<typeof importFormSchema>;

export interface ILayerCatalogImportResult {
  levels: ILayerCatalogLevels;
  mode: LayerCatalogImportMode;
}

export interface ILayerCatalogImportModalProps {
  open: boolean;
  onConfirm: (result: ILayerCatalogImportResult) => void;
  onClose: () => void;
}

/**
 * Modal per importare livelli e gruppi da un catalogo esistente.
 * L'utente seleziona un catalogo, spunta i livelli/gruppi da importare
 * nell'anteprima ad albero, e sceglie la modalità di importazione
 * (append o replace). Alla conferma il form invia solo le voci selezionate.
 */
export function LayerCatalogImportModal(props: ILayerCatalogImportModalProps) {
  const { open, onConfirm, onClose } = props;

  // Il DTO del catalogo e l'anteprima derivata sono oggetti pesanti/derivati,
  // non input utente: restano stato locale, aggiornati da onAfterChange della
  // search select. Nel form vivono solo catalogId, selectedKeys e mode.
  const [selected, setSelected] = useState<MappaCatalogoLivelliDTO | null>(
    null,
  );

  const preview = useMemo<ILayerCatalogLevels | null>(
    () => (selected ? parseLayerCatalogLevels(selected.livelli) : null),
    [selected],
  );

  // Reinizializza il form al cambio di catalogo: FormHandler resetta ai nuovi
  // initialValues, quindi la selezione torna a "tutto selezionato" (gruppi
  // inclusi esplicitamente, il nodo virtuale lo deriva Ant dalla cascata).
  const initialValues = useMemo(
    () => ({
      catalogId: selected?.id != null ? String(selected.id) : null,
      selectedKeys: preview
        ? getSelectAllKeys(preview.groups, preview.layers)
        : [],
      mode: 'append' as LayerCatalogImportMode,
    }),
    [selected, preview],
  );

  const handleClose = useCallback(() => {
    setSelected(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(
    async (data: IImportFormValues) => {
      if (!preview) return;
      const levels = filterCatalogLevelsBySelection(
        preview,
        data.selectedKeys ?? [],
      );
      if (levels.layers.length === 0 && levels.groups.length === 0) return;

      const mode = data.mode as LayerCatalogImportMode;
      if (mode === 'replace') {
        const confirmed = await modalConfirm({
          title: 'Conferma sostituzione',
          content:
            'Tutti i livelli e i gruppi esistenti verranno eliminati e sostituiti con quelli del catalogo selezionato. Continuare?',
          okText: 'Sostituisci',
          okType: 'danger',
          cancelText: 'Annulla',
        });
        if (!confirmed) return;
      }

      onConfirm({ levels, mode });
      setSelected(null);
    },
    [preview, onConfirm],
  );

  return (
    <Modal
      open={open}
      title="Importa dal Catalogo Livelli"
      onCancel={handleClose}
      destroyOnClose
      width={640}
      footer={null}
    >
      <FormHandler
        nested
        hideVisualFeedback
        schema={importFormSchema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Typography.Text strong>Cerca nel catalogo</Typography.Text>
            <Space.Compact block style={{ marginTop: 8 }}>
              <LayerCatalogSearchSelect
                name="catalogId"
                style={{ width: '100%' }}
                size="large"
                placeholder="Cerca per denominazione..."
                onAfterChange={setSelected}
              />
              {selected?.id && (
                <LayerCatalogEditLinkButton catalogId={selected.id} />
              )}
            </Space.Compact>
          </div>

          {preview && (
            <>
              <LayerCatalogPreview
                groups={preview.groups}
                layers={preview.layers}
                name="selectedKeys"
              />

              <FormItem name="mode" label="Modalità di importazione">
                <RadioGroupInput name="mode" vertical>
                  <Radio value="append">
                    Aggiungi — aggiunge i livelli e i gruppi selezionati a
                    quelli esistenti
                  </Radio>
                  <Radio value="replace">
                    Sostituisci — rimuovi i livelli/gruppi attuali e inserisci
                    quelli del catalogo
                  </Radio>
                </RadioGroupInput>
              </FormItem>
            </>
          )}

          <FlexRow
            style={{ marginTop: 8 }}
            right={
              <Space>
                <Button size="large" onClick={handleClose}>
                  Annulla
                </Button>
                <LayerCatalogImportSubmitButton preview={preview} />
              </Space>
            }
          />
        </Space>
      </FormHandler>
    </Modal>
  );
}
